import { useCallback, useMemo, useState } from 'react'
import { isAfter, parseISO, startOfDay } from 'date-fns'
import { useToast } from '../../../Hooks/ToasContext'
import {
  ITiragemProdutoConfig,
  getConfig,
  saveConfig,
  getProdutoConfig,
} from '../../../Config/hcp-print-run-config'
import api from '../../../Services/api'
import { IOrderEntity } from '../../../Services/api/orders/orders.interface'
import { IProductEntity } from '../../../Services/api/products/products.interface'

export type AbaMontagemType = 'sem_os' | 'com_os'
export type FiltroItensPendentesType = 'todos' | 'completos' | 'com_pendencias'

interface IProductionType {
  name: string
  sheet?: number
  production?: number
  machineProduction?: number
  logistics?: number
}

interface IHCPCurrentStatus {
  flow: string
  flowCode: number
  sequence: number
  displayCode: string
}

export interface IOrderProductHCPFlow {
  id: string | number
  createdAt: string
  updatedAt: string
  order?: IOrderEntity | null
  product?: (IProductEntity & { productionType?: IProductionType | null }) | null
  totalQuantity?: number | string | null
  productOrderCode?: string | null
  customerDeadline?: string | null
  artLink?: string | null
  serviceOrderId?: string | number | null
  hcpCurrentFlow?: string | null
  hcpCurrentFlowStartedAt?: string | null
  hcpCurrentFlowDeadline?: string | null
  hcpCurrentStatus?: IHCPCurrentStatus | null
  hcpFlowHistories?: any[]
  hcpFinished?: boolean
  allArtsApproved?: boolean | null
}

interface IOrderProductsHCPFlowResponse {
  data: IOrderProductHCPFlow[]
  total: number
  hasNextPage: boolean
}

export interface IMontagemGrupoProduto {
  produto_nome: string
  totalTiragem: number
  minimoTiragem: number
  meiaCartela?: number
  podeFecharCartela: boolean
  meiaCartelaDisponivel: boolean
  qtdMeiaCartelas: number
  prazoMinimo: string | null
  itens: IOrderProductHCPFlow[]
}

export interface IMontagemGrupoOS {
  ordem_de_servico: string
  produto_nome: string
  etapa: string
  prazo_producao: string
  data_inicio: string
  totalTiragem: number
  atrasado: boolean
  itens: IOrderProductHCPFlow[]
}

const SHEET_PAGE_SIZE = 100

function getAllArtsApprovedParam(filtro: FiltroItensPendentesType): boolean | undefined {
  if (filtro === 'completos') return true
  if (filtro === 'com_pendencias') return false
  return undefined
}

async function fetchTodasPaginasSheet(
  allArtsApproved?: boolean
): Promise<{ items: IOrderProductHCPFlow[]; totalCount: number }> {
  const allItems: IOrderProductHCPFlow[] = []
  let page = 1
  let hasMore = true
  let totalCount = 0

  while (hasMore) {
    const params: { page: number; offset: number; allArtsApproved?: boolean } = {
      page,
      offset: SHEET_PAGE_SIZE,
    }

    if (allArtsApproved !== undefined) {
      params.allArtsApproved = allArtsApproved
    }

    const response = await api.get<IOrderProductsHCPFlowResponse>('/order-products/hcp-flow/SHEET', {
      params,
    })

    totalCount = response.data.total
    allItems.push(...response.data.data)
    hasMore = response.data.hasNextPage
    page++
  }

  return { items: allItems, totalCount }
}

function getTiragem(item: IOrderProductHCPFlow): number {
  return Number(item.totalQuantity) || 0
}

function getProdutoNome(item: IOrderProductHCPFlow): string {
  return item.product?.name ?? 'Produto sem nome'
}

function hasServiceOrder(item: IOrderProductHCPFlow): boolean {
  return Boolean(item.serviceOrderId)
}

export function getItensPendentesPedido(item: IOrderProductHCPFlow): number {
  if (item.allArtsApproved === true) return 0

  const produtosPedido = item.order?.products ?? []

  if (produtosPedido.length === 0) {
    return item.allArtsApproved === false ? 1 : item.artLink ? 0 : 1
  }

  const itensPendentes = produtosPedido.filter((produto) => !produto.artApprovedDate && !produto.artLink).length

  if (item.allArtsApproved === false && itensPendentes === 0) return 1

  return itensPendentes
}

export function useMontagemCartelas() {
  const [aba, setAba] = useState<AbaMontagemType>('sem_os')
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [itensSemOS, setItensSemOS] = useState<IOrderProductHCPFlow[]>([])
  const [itensComOS, setItensComOS] = useState<IOrderProductHCPFlow[]>([])
  const [comOSTotal, setComOSTotal] = useState(0)
  const [comOSHasMore, setComOSHasMore] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>(undefined)
  const [filtroBusca, setFiltroBusca] = useState('')
  const [filtroItensPendentes, setFiltroItensPendentes] = useState<FiltroItensPendentesType>('todos')
  const [tiragemConfig, setTiragemConfig] = useState<Record<string, ITiragemProdutoConfig>>(() => getConfig())
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const { showToast } = useToast()

  const fetchDados = useCallback(async () => {
    setLoading(true)

    try {
      const result = await fetchTodasPaginasSheet(getAllArtsApprovedParam(filtroItensPendentes))
      const semOS = result.items.filter((item) => !hasServiceOrder(item))
      const comOS = result.items.filter(hasServiceOrder)

      setItensSemOS(semOS)
      setItensComOS(comOS)
      setComOSTotal(comOS.length)
      setComOSHasMore(false)
      setLastUpdated(new Date())
    } catch (error: any) {
      showToast(String(error?.response?.data?.error ?? error?.message ?? 'Erro ao carregar dados de SHEET'), 'error')
    } finally {
      setLoading(false)
    }
  }, [filtroItensPendentes, showToast])

  const loadMoreComOS = async () => {
    setLoadingMore(false)
  }

  const salvarConfiguracao = useCallback((novaConfig: Record<string, ITiragemProdutoConfig>) => {
    saveConfig(novaConfig)
    setTiragemConfig(novaConfig)
    setConfigDialogOpen(false)
  }, [])

  const grupos = useMemo((): IMontagemGrupoProduto[] => {
    const map = new Map<string, IOrderProductHCPFlow[]>()
    itensSemOS.forEach((item) => {
      const produtoNome = getProdutoNome(item)
      if (!map.has(produtoNome)) map.set(produtoNome, [])
      map.get(produtoNome)!.push(item)
    })

    const lista: IMontagemGrupoProduto[] = []
    map.forEach((grupoItens, produto_nome) => {
      const totalTiragem = grupoItens.reduce((acc, item) => acc + getTiragem(item), 0)
      const cfg = getProdutoConfig(produto_nome, tiragemConfig)
      const minimoTiragem = cfg.minimoCartela
      const meiaCartela = cfg.meiaCartela
      const podeFecharCartela = totalTiragem >= minimoTiragem
      const qtdMeiaCartelas = meiaCartela ? Math.floor(totalTiragem / meiaCartela) : 0
      const meiaCartelaDisponivel = !podeFecharCartela && qtdMeiaCartelas >= 1
      const prazoMinimo = grupoItens
        .map(i => i.hcpCurrentFlowDeadline)
        .filter((p): p is string => Boolean(p))
        .sort()[0] ?? null

      lista.push({
        produto_nome,
        totalTiragem,
        minimoTiragem,
        meiaCartela,
        podeFecharCartela,
        meiaCartelaDisponivel,
        qtdMeiaCartelas,
        prazoMinimo,
        itens: grupoItens,
      })
    })

    lista.sort((a, b) => {
      if (a.podeFecharCartela !== b.podeFecharCartela) return a.podeFecharCartela ? -1 : 1
      if (a.meiaCartelaDisponivel !== b.meiaCartelaDisponivel) return a.meiaCartelaDisponivel ? -1 : 1
      return b.totalTiragem - a.totalTiragem
    })

    return lista
  }, [itensSemOS, tiragemConfig])

  const gruposOS = useMemo((): IMontagemGrupoOS[] => {
    const map = new Map<string, IOrderProductHCPFlow[]>()
    itensComOS.forEach((item) => {
      const key = item.serviceOrderId ? String(item.serviceOrderId) : 'Sem OS'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    })

    const lista: IMontagemGrupoOS[] = []
    map.forEach((osItens, ordem_de_servico) => {
      const totalTiragem = osItens.reduce((acc, item) => acc + getTiragem(item), 0)
      const prazo_producao = osItens
        .map((i) => i.hcpCurrentFlowDeadline)
        .filter((p): p is string => Boolean(p))
        .sort()
        .at(-1) ?? ''
      const hoje = startOfDay(new Date())
      const atrasado = prazo_producao !== '' && !isAfter(parseISO(prazo_producao), hoje)
      const data_inicio = osItens
        .map((i) => i.hcpCurrentFlowStartedAt)
        .filter((p): p is string => Boolean(p))
        .sort()[0] ?? ''
      const primeiro = osItens[0]
      const etapa = primeiro?.hcpCurrentFlow ?? ''
      const produto_nome = primeiro ? getProdutoNome(primeiro) : ''

      lista.push({ ordem_de_servico, produto_nome, etapa, prazo_producao, data_inicio, totalTiragem, atrasado, itens: osItens })
    })

    lista.sort((a, b) => {
      if (a.atrasado !== b.atrasado) return a.atrasado ? -1 : 1
      return a.prazo_producao.localeCompare(b.prazo_producao)
    })
    return lista
  }, [itensComOS])

  const produtosDisponiveis = useMemo(() => {
    const set = new Set<string>()
    itensSemOS.forEach((i) => set.add(getProdutoNome(i)))
    Object.keys(tiragemConfig).forEach((k) => set.add(k))
    return Array.from(set).sort()
  }, [itensSemOS, tiragemConfig])

  const gruposFiltrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    if (!busca) return grupos
    return grupos.filter((g) => g.produto_nome.toLowerCase().includes(busca))
  }, [grupos, filtroBusca])

  const gruposOSFiltrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    if (!busca) return gruposOS
    return gruposOS.filter(
      (g) => g.ordem_de_servico.toLowerCase().includes(busca) || g.produto_nome.toLowerCase().includes(busca)
    )
  }, [gruposOS, filtroBusca])

  return {
    aba, setAba,
    grupos: gruposFiltrados,
    gruposOS: gruposOSFiltrados,
    totalSemOS: itensSemOS.length,
    totalComOS: comOSTotal,
    comOSCarregados: itensComOS.length,
    comOSHasMore,
    loadingMore,
    loadMoreComOS,
    loading, lastUpdated,
    filtroBusca, setFiltroBusca,
    filtroItensPendentes, setFiltroItensPendentes,
    fetchDados,
    tiragemConfig,
    produtosDisponiveis,
    configDialogOpen,
    setConfigDialogOpen,
    salvarConfiguracao,
  }
}
